import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import { fetchPublicPage } from "../lib/api";

const KNOWN_SLUGS = new Set(["privacy", "refund", "terms", "about", "contact"]);

/** Public CMS page renderer. Reads /api/pages/:slug (no auth) and displays admin-owned
 *  HTML. Body is trusted — admins control the markup, no user input reaches this path. */
export function PublicPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!KNOWN_SLUGS.has(slug)) {
      setLoading(false);
      return;
    }
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchPublicPage(slug);
        if (alive) setPage(data);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Couldn't load this page.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  if (!KNOWN_SLUGS.has(slug)) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted">
        Page not found. <Link className="text-gold underline" to="/">Go home</Link>.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:py-14" data-testid={`page-${slug}`}>
      <Link
        to="/"
        className="mb-6 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted hover:text-gold"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back home
      </Link>
      {loading ? (
        <div className="flex items-center gap-2 text-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : page ? (
        <>
          <h1 className="mb-6 font-heading text-3xl font-semibold text-text sm:text-4xl">
            {page.title}
          </h1>
          {/* Admins own the markup here — trusted content from Admin → Pages. `prose`
              gives us reasonable typography without extra CSS. */}
          <div
            className="prose prose-invert max-w-none text-text [&_a]:text-gold [&_h2]:text-text [&_h3]:text-text"
            data-testid={`page-${slug}-body`}
            dangerouslySetInnerHTML={{ __html: page.body_html }}
          />
          {page.updated_at && (
            <p className="mt-10 text-xs text-muted">
              Last updated: {new Date(page.updated_at).toLocaleDateString()}
            </p>
          )}
        </>
      ) : null}
    </div>
  );
}

export default PublicPage;
